import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import Button from '../components/ui/Button';
import { db } from '../lib/db';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { FaStar } from 'react-icons/fa';

const { FiDownload, FiTrash2, FiLoader, FiMessageSquare, FiArrowLeft } = FiIcons;

const ReviewsDashboard = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = async () => {
    setLoading(true);
    const data = await db.getAllNegativeReviews();
    setReviews(data);
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this feedback?')) return;
    const success = await db.deleteNegativeReview(id);
    if (success) loadReviews();
  };

  const handleExport = () => {
    if (reviews.length === 0) return;
    
    const headers = ["Date", "Tag ID", "Rating", "Name", "Feedback"];
    const csvContent = [
      headers.join(","),
      ...reviews.map(r => [
        new Date(r.created_at).toLocaleString(),
        r.tag_code,
        r.rating,
        `"${(r.user_name || '').replace(/"/g, '""')}"`,
        `"${(r.feedback || '').replace(/"/g, '""')}"`
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "negative_reviews.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Layout>
      <div className="space-y-8 pb-20">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => window.history.back()} className="w-12 h-12 p-0 flex items-center justify-center">
            <SafeIcon icon={FiArrowLeft} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Negative Reviews</h1>
            <p className="text-zinc-500 text-sm">Feedback captured from 1-3 star ratings.</p>
          </div>
        </div>

        <div className="flex justify-between items-center">
          <div className="text-sm text-zinc-400">
            Total Feedback: <span className="text-white font-bold">{reviews.length}</span>
          </div>
          {reviews.length > 0 && (
            <Button onClick={handleExport} variant="outline" className="w-auto py-2 px-4 text-sm">
              <SafeIcon icon={FiDownload} /> Export CSV
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <SafeIcon icon={FiLoader} className="animate-spin text-3xl text-zinc-600" />
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-20 border-2 border-dashed border-zinc-800 rounded-3xl bg-zinc-900/20">
            <div className="w-16 h-16 bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4 text-zinc-600">
              <SafeIcon icon={FiMessageSquare} className="text-2xl" />
            </div>
            <p className="text-zinc-500">No negative reviews yet.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {reviews.map((review) => (
              <div key={review.id} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 hover:border-zinc-700 transition-colors">
                <div className="flex justify-between items-start gap-4 mb-3">
                  <div className="flex items-center gap-2">
                    <div className="bg-amber-500/10 border border-amber-500/20 px-2 py-1 rounded-lg flex items-center gap-1">
                      <SafeIcon icon={FaStar} className="text-amber-500 text-xs" />
                      <span className="text-amber-500 font-bold text-sm">{review.rating}</span>
                    </div>
                    <span className="text-zinc-500 text-xs">
                      {review.tag_code} • {new Date(review.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <button 
                    onClick={() => handleDelete(review.id)}
                    className="text-zinc-600 hover:text-red-500 transition-colors"
                  >
                    <SafeIcon icon={FiTrash2} />
                  </button>
                </div>
                <p className="text-white text-sm leading-relaxed mb-2">
                  "{review.feedback}"
                </p>
                {review.user_name && (
                  <p className="text-xs text-zinc-500 font-medium">
                    - {review.user_name}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ReviewsDashboard;